/**
 * v3.99d.282 — Username chooser modal (Phase 2 of the collector privacy
 * audit). Appears once for any signed-in user whose username is still the
 * cryptographically-random `Collector-XXXXX` temp handle assigned at
 * signup or by the backfill migration. Lets them claim a permanent
 * public-safe username with live availability + strict server-side
 * validation.
 *
 * Privacy contract surfaced in UI:
 *   - Username is the ONLY public identity field
 *   - Legal name, email, address, phone are never shown publicly
 *   - Server is the source of truth for validation rules
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ShieldCheck, Loader2, Check, X } from "lucide-react";
import { api, formatApiError } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function UsernameChooserModal() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [check, setCheck] = useState(null); // { available, reason, normalized }
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(null);

  // Show only for real signed-in users with a temp handle. Evaluators are
  // synthetic and skip this surface.
  useEffect(() => {
    if (!user || user === false || user._eval) return;
    if (user.username_is_temporary && user.username) {
      // One-shot per browser session — don't re-pester the same tab on every
      // route change. Account → Privacy still surfaces the chooser inline.
      const seen = sessionStorage.getItem("cf_username_prompt_seen");
      if (!seen) {
        setOpen(true);
        sessionStorage.setItem("cf_username_prompt_seen", "1");
      }
    }
  }, [user]);

  // Debounced live availability probe.
  useEffect(() => {
    if (!value || value.length < 3) { setCheck(null); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setChecking(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/users/check-username", { params: { username: value } });
        setCheck(data);
      } catch (e) {
        setCheck({ available: false, reason: formatApiError(e) || "Could not check" });
      } finally {
        setChecking(false);
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value]);

  const canSubmit = useMemo(
    () => !!check && check.available && !submitting,
    [check, submitting]
  );

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/users/me/username", { username: value });
      await refresh();
      toast.success("Username saved. This is what other users see.");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not save username");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !user || user === false) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(8px)" }}
      data-testid="username-chooser-modal"
    >
      <div
        className="max-w-md w-full bg-[var(--surface)] border border-[var(--border-strong)] p-7"
        style={{ borderRadius: 2 }}
      >
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-4 h-4" style={{ color: "var(--gold)" }} />
          <div className="text-[10px] font-mono uppercase tracking-[0.22em]" style={{ color: "var(--gold)" }}>
            Private by default
          </div>
        </div>
        <h2 className="font-serif text-2xl tracking-tighter mb-2" style={{ color: "var(--text)" }}>
          Pick a public username
        </h2>
        <p className="text-sm mb-5" style={{ color: "var(--text-2)" }}>
          Only your username appears publicly on CannaForge — auction bids,
          grow logs, giveaways, reviews, leaderboards. Your legal name, email,
          shipping address, and payment details stay private.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] block mb-1" style={{ color: "var(--text-muted)" }}>
              Username
            </span>
            <input
              type="text"
              required
              autoFocus
              minLength={3}
              maxLength={24}
              value={value}
              onChange={(e) => setValue(e.target.value.trim())}
              className="input-field"
              placeholder="e.g. PublicWolf, AlpineGrows, CollectorK"
              data-testid="username-chooser-input"
            />
            <div className="mt-2 min-h-[18px] flex items-center gap-1.5 text-[11px] font-mono">
              {checking && (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" style={{ color: "var(--text-muted)" }} />
                  <span style={{ color: "var(--text-muted)" }}>Checking…</span>
                </>
              )}
              {!checking && check && check.available && (
                <>
                  <Check className="w-3 h-3" style={{ color: "#7FCB99" }} />
                  <span style={{ color: "#7FCB99" }} data-testid="username-chooser-available">
                    {check.normalized || value} is available
                  </span>
                </>
              )}
              {!checking && check && !check.available && (
                <>
                  <X className="w-3 h-3" style={{ color: "#FFB48F" }} />
                  <span style={{ color: "#FFB48F" }} data-testid="username-chooser-error">
                    {check.reason || "Unavailable"}
                  </span>
                </>
              )}
            </div>
          </label>

          <div className="text-[10px] font-mono uppercase tracking-[0.18em] leading-5" style={{ color: "var(--text-muted)" }}>
            <div>3–24 chars · letters, numbers, dots, dashes, underscores</div>
            <div>No emails · no phone numbers · no impersonation</div>
          </div>

          <button
            type="submit"
            disabled={!canSubmit}
            className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="username-chooser-submit"
          >
            {submitting ? "Saving…" : "Save my username"}
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="w-full text-[11px] font-mono uppercase tracking-[0.22em] py-2"
            style={{ color: "var(--text-muted)" }}
            data-testid="username-chooser-skip"
          >
            Decide later
          </button>
          <div className="text-[10px] text-center" style={{ color: "var(--text-muted)" }}>
            You can change this once every 7 days from Account settings.
          </div>
        </form>
      </div>
    </div>
  );
}
